import { FC, useMemo } from "react";
import { addDays, format, parseISO } from "date-fns";
import { fullDateLongFormat } from "@common/helpers/date";
import { VStack } from "@design-system/VStack/VStack";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import classNames from "classnames";
import styles from "./HistoricalDataFields.module.scss";

interface Props {
    startDate: string | undefined;
    endDate: string | undefined;
    onStartDateChange: (value: string | undefined) => void;
    // When omitted, the end date is rendered read-only (e.g. derived from the
    // entity bookkeeping start date in the migration modal).
    onEndDateChange?: (value: string) => void;
    isStartDateDisabled?: boolean;
    startDateDisabledReason?: string;
}

const noop = () => {};

export const MigrationDateRange: FC<Props> = ({
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    isStartDateDisabled = false,
    startDateDisabledReason,
}) => {
    const cutoverDate = useMemo(() => {
        if (!endDate) {
            return null;
        }
        try {
            return format(addDays(parseISO(endDate), 1), fullDateLongFormat);
        } catch {
            return null;
        }
    }, [endDate]);

    const isEndDateReadOnly = onEndDateChange === undefined;

    return (
        <VStack
            gap={8}
            paddingBlock={16}
            paddingInline={24}
            className={styles.migrationDateRange}
        >
            <div className={styles.migrationSectionLabel}>
                Migration date range
            </div>
            <div className={styles.migrationDateRangeRow}>
                <VStack gap={6} className={styles.migrationDateRangeField}>
                    <div className={styles.migrationDateRangeFieldLabel}>
                        Start date
                        {!isStartDateDisabled && (
                            <>
                                {" "}
                                <span className={styles.optionalSuffix}>
                                    (optional)
                                </span>
                            </>
                        )}
                    </div>
                    <div className={styles.migrationDatePickerContainer}>
                        <Tooltip
                            content={
                                isStartDateDisabled
                                    ? startDateDisabledReason
                                    : undefined
                            }
                        >
                            <DatePicker
                                mode="day"
                                variant="panel-input"
                                label={
                                    isStartDateDisabled
                                        ? "Start date"
                                        : "Start date (optional)"
                                }
                                placeholder="Select"
                                clearable={!isStartDateDisabled}
                                showTriggerIcon={false}
                                value={startDate}
                                disabled={isStartDateDisabled}
                                onValueChange={onStartDateChange}
                            />
                        </Tooltip>
                    </div>
                </VStack>
                <VStack gap={6} className={styles.migrationDateRangeField}>
                    <div className={styles.migrationDateRangeFieldLabel}>
                        End date
                    </div>
                    <div className={styles.migrationDatePickerContainer}>
                        <DatePicker
                            mode="day"
                            variant="panel-input"
                            label="End date"
                            placeholder="Select"
                            clearable={false}
                            showTriggerIcon={false}
                            value={endDate}
                            disabled={isEndDateReadOnly}
                            onValueChange={(value) => {
                                if (value) {
                                    (onEndDateChange ?? noop)(value);
                                }
                            }}
                        />
                    </div>
                </VStack>
            </div>
            {!isStartDateDisabled && (
                <div className={styles.migrationDateRangeNote}>
                    Leave start date blank to import all historical journal
                    entries
                </div>
            )}
            {cutoverDate && (
                <div className={styles.migrationDateRangeNote}>
                    Kick will take over on {cutoverDate}
                </div>
            )}
            {!!startDate && !!endDate && startDate > endDate && (
                <div
                    className={classNames(
                        styles.migrationDateRangeNote,
                        styles.errorMessage,
                    )}
                >
                    End date must be after start date
                </div>
            )}
        </VStack>
    );
};
